import { Platform } from 'react-native';

import { SPARKS, sparkCycleMs, type SparkSpec } from './auraTiming';

/**
 * CORE-1238 — the launcher aura animates in CSS, not with `Animated`.
 *
 * react-native-web has no native animated module, so every `Animated` loop
 * runs on a `requestAnimationFrame` tick and ends in a React commit. The aura
 * lives in a widget mounted at the app root — outside the Suspense boundary
 * around the routes — and its two endless loops alone produced ~60 commits a
 * second on every screen (measured on dev: 301 commits in 5 s, 0 with reduced
 * motion).
 *
 * That was not just wasted CPU. Opening a route suspends the navigator, and
 * React re-renders it on a retry lane that never expires. On a slow CPU the
 * dashboard's first render takes longer than a frame, so each aura commit
 * interrupted it, the retry started over, and the page sat on the global
 * loader for good: headless Chromium at 8× CPU throttling stayed on the loader
 * for 90 s+ with the aura running and opened the dashboard in 2.4 s with it
 * stopped. That is the web:stage «dashboard never loads» of CORE-1238 — the
 * RU runner has two cores and runs specs in parallel.
 *
 * `transform` and `opacity` keyframes run on the compositor and never touch
 * React. Timings come from `auraTiming`, so the rhythm stays the one the
 * design asked for (CORE-847 §3, CORE-1105).
 */
const STYLE_ID = 'agp-launcher-aura-anim';

export const GLOW_PERIOD_MS = 3000;
export const RING_SWEEP_MS = 9000;

/** Glow opacity range: idle, and «leaning in» per theme (hover / focus / open chat). */
export const BREATH_RANGES = {
  idle: [0.28, 0.44],
  activeDark: [0.46, 0.68],
  activeLight: [0.65, 0.92],
} as const;

export type BreathVariant = keyof typeof BREATH_RANGES;

const pct = (n: number) => `${Math.round(n * 10000) / 100}%`;

/**
 * One spark: a flash at the start of its cycle, quiet for the rest. The first
 * flash waits `delay` (animation-delay); after that the period is
 * `delay + duration + rest` — the same gap between flashes the timers gave.
 */
export function sparkKeyframes(spec: SparkSpec, index: number): string {
  const cycle = sparkCycleMs(spec);
  const peak = pct(spec.duration / 2 / cycle);
  const out = pct(spec.duration / cycle);
  return `@keyframes agp-aura-spark-${index} {
  0% { opacity: 0; transform: scale(0.4); animation-timing-function: ease-out; }
  ${peak} { opacity: 1; transform: scale(1); animation-timing-function: ease-in; }
  ${out}, 100% { opacity: 0; transform: scale(0.4); }
}
[data-agp-aura-spark="${index}"] {
  opacity: 0;
  animation: agp-aura-spark-${index} ${cycle}ms linear ${spec.delay}ms infinite;
}`;
}

export function buildAuraCss(sparks: SparkSpec[] = SPARKS): string {
  const breath = (Object.keys(BREATH_RANGES) as BreathVariant[]).map((variant) => {
    const [low, high] = BREATH_RANGES[variant];
    return `@keyframes agp-aura-breath-${variant} {
  0%, 100% { opacity: ${low}; transform: scale(0.94); }
  50% { opacity: ${high}; transform: scale(1.04); }
}
[data-agp-aura-breath="${variant}"] {
  animation: agp-aura-breath-${variant} ${GLOW_PERIOD_MS}ms ease-in-out infinite;
}`;
  });
  return [
    ...breath,
    `@keyframes agp-aura-sweep {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
[data-agp-aura-sweep="true"] {
  animation: agp-aura-sweep ${RING_SWEEP_MS}ms linear infinite;
}`,
    ...sparks.map(sparkKeyframes),
  ].join('\n');
}

/** Idempotent; a no-op off the web. */
export function ensureAuraKeyframes() {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = buildAuraCss();
  document.head.appendChild(style);
}
